import React, { useCallback, useEffect, useState } from "react";
import {
  Box, Button, Chip, IconButton, Stack, Tooltip, Typography, Alert,
} from "@mui/material";
import { failureLabel } from "../shared/failureLabels";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import CancelScheduleSendOutlinedIcon from "@mui/icons-material/CancelScheduleSendOutlined";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { useNavigate, useParams } from "react-router-dom";
import {
  archiveCampaign, cancelSchedule, fetchCampaign, ICampaign,
} from "../../../Api/campaignsApi";
import { useNotifier } from "../../../Core/Notifier";
import { useConfirm } from "../../../Components/Confirm";
import { B13, B13Ink, B13TypeScale, tint12 } from "../../../theme/b13Tokens";
import { surfaceSx } from "../../Course/shared/moduleSx";

const CampaignDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const confirm = useConfirm();

  const [campaign, setCampaign]         = useState<ICampaign | null>(null);
  const [stats, setStats]               = useState<any | null>(null);
  const [loading, setLoading]           = useState(true);

  const load = useCallback(async () => {
    if (! id) return;
    setLoading(true);
    try {
      const res = await fetchCampaign(Number(id));
      setCampaign(res.campaign);
      setStats(res);
    } catch (e: any) {
      showErrorMessage(e?.message || "Failed to load campaign.");
    } finally { setLoading(false); }
  }, [id, showErrorMessage]);

  useEffect(() => { load(); }, [load]);

  if (loading || ! campaign) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography sx={{ color: B13Ink.ink2 }}>Loading…</Typography>
      </Box>
    );
  }

  const isMarketing  = campaign.classification === "marketing";
  const Channel      = campaign.channel === "email" ? EmailOutlinedIcon : SmsOutlinedIcon;
  const isEditable   = campaign.status === "draft" || campaign.status === "scheduled";

  const handleCancelSchedule = async () => {
    await confirm({
      title: "Cancel scheduled send?",
      message: "This campaign will return to draft. You can reschedule or send it later.",
      confirmText: "Cancel schedule",
      cancelText: "Keep scheduled",
      onConfirm: async () => {
        try {
          await cancelSchedule(campaign.id);
          showMessage("Schedule cancelled.");
          load();
        } catch (e: any) {
          showErrorMessage(e?.message || "Failed to cancel schedule.");
          throw e;
        }
      },
    });
  };

  const handleArchive = async () => {
    await confirm({
      title: "Archive this campaign?",
      message: "Archived campaigns are hidden from the default list but their history is preserved.",
      confirmText: "Archive",
      variant: "danger",
      onConfirm: async () => {
        try {
          await archiveCampaign(campaign.id);
          showMessage("Archived.");
          load();
        } catch (e: any) {
          showErrorMessage(e?.message || "Failed to archive.");
          throw e;
        }
      },
    });
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: "#F7F9FC", minHeight: "100%" }}>
      {/* Header */}
      <Box sx={{ ...surfaceSx, p: 2, mb: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}
               alignItems={{ xs: "flex-start", md: "center" }}>
          <Tooltip title="Back to campaigns">
            <IconButton onClick={() => navigate("/communication/newsletters")}
              sx={{ width: 40, height: 40, borderRadius: "10px",
                    bgcolor: tint12(B13.primary), color: B13.primary,
                    "&:hover": { bgcolor: B13.primary, color: "#fff" } }}
            ><ArrowBackIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Box sx={{
            width: 48, height: 48, borderRadius: 2.5,
            bgcolor: tint12(B13.primary), color: B13.primary,
            display: "flex", alignItems: "center", justifyContent: "center",
          }}><Channel /></Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ ...B13TypeScale.h2, letterSpacing: "-0.01em" }}>{campaign.name}</Typography>
            <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
              <Chip size="small" label={campaign.channel.toUpperCase()}
                sx={{ height: 20, fontSize: 10.5, bgcolor: tint12(B13.primary), color: B13.primary, fontWeight: 700 }} />
              <Chip size="small" label={campaign.classification}
                sx={{ height: 20, fontSize: 10.5, textTransform: "capitalize",
                      bgcolor: isMarketing ? "#FEF3C7" : "#DCFCE7",
                      color:   isMarketing ? "#92400E" : "#15803D", fontWeight: 700 }} />
              <Chip size="small" label={campaign.purpose}
                sx={{ height: 20, fontSize: 10.5, bgcolor: "#F1F5F9", color: B13Ink.ink2, textTransform: "capitalize" }} />
              <Chip size="small" label={campaign.status.replace("_", " ")}
                sx={{ height: 20, fontSize: 10.5, textTransform: "capitalize", fontWeight: 700,
                      bgcolor: "#F1F5F9", color: B13.ink }} />
            </Stack>
          </Box>
          <Stack direction="row" spacing={1}>
            {isEditable && (
              <Button variant="outlined" onClick={() => navigate(`/communication/newsletters/${campaign.id}/edit`)}
                sx={{ textTransform: "none", borderRadius: "10px" }}>Edit</Button>
            )}
            {campaign.status === "scheduled" && (
              <Button variant="outlined" startIcon={<CancelScheduleSendOutlinedIcon />}
                onClick={handleCancelSchedule} sx={{ textTransform: "none", borderRadius: "10px" }}>
                Cancel schedule
              </Button>
            )}
            {campaign.status !== "archived" && (
              <Button variant="outlined" startIcon={<Inventory2OutlinedIcon />}
                onClick={handleArchive} sx={{ textTransform: "none", borderRadius: "10px" }}>
                Archive
              </Button>
            )}
          </Stack>
        </Stack>
      </Box>

      {/* Metrics — real backend numbers only. Delivered deliberately
          absent (§11). Opened/Open Rate only shown for email
          campaigns and only when real tracking data exists (§1). */}
      <Box sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "repeat(2, 1fr)",
          md: campaign.channel === "email" ? "repeat(6, 1fr)" : "repeat(4, 1fr)",
        },
        gap: 1.5, mb: 2,
      }}>
        <Metric label="Recipients" value={stats?.source_count ?? campaign.total_recipients} />
        <Metric label="Sent"       value={campaign.sent_count}    tone="#15803D" />
        {campaign.channel === "email" && (
          <>
            <Metric
              label="Opened"
              value={stats?.opens?.unique_opens ?? 0}
              tone="#5B21B6"
              tooltip="Open tracking is approximate and may be affected by email privacy features."
            />
            <Metric
              label="Open rate"
              value={
                stats?.opens?.open_rate === null || stats?.opens?.open_rate === undefined
                  ? "—"
                  : `${stats.opens.open_rate}%`
              }
              tone="#5B21B6"
              tooltip="Unique opens ÷ non-test sent emails. Open tracking is approximate."
            />
          </>
        )}
        <Metric label="Skipped"    value={campaign.skipped_count} tone="#92400E" />
        <Metric label="Failed"     value={campaign.failed_count}  tone="#B91C1C" />
      </Box>

      {campaign.status === "failed" && stats?.source_count === 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          This campaign completed with no provider messages sent — no eligible recipients.
        </Alert>
      )}

      {/* Content preview */}
      <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3, mb: 2 }}>
        <Typography sx={{ ...B13TypeScale.h3, mb: 2 }}>Content</Typography>
        {campaign.channel === "email" && (
          <Box sx={{ mb: 1.5 }}>
            <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6 }}>
              Subject
            </Typography>
            <Typography sx={{ fontSize: 15, fontWeight: 700, mt: 0.5 }}>{campaign.subject || "—"}</Typography>
          </Box>
        )}
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6, mt: 1 }}>
          Body
        </Typography>
        <Box sx={{ mt: 1, border: `1px solid ${B13.line}`, borderRadius: 2, p: 2,
                   bgcolor: "#FAFBFC", fontSize: 13.5, whiteSpace: "pre-wrap" }}>
          {campaign.body}
        </Box>
      </Box>

      {/* Skip breakdown — labels come from the shared failureLabel
          map so operators see human-friendly copy. */}
      {stats?.skip_reason_breakdown && Object.keys(stats.skip_reason_breakdown).length > 0 && (
        <Box sx={{ ...surfaceSx, p: 3, borderRadius: 3 }}>
          <Typography sx={{ ...B13TypeScale.h3, mb: 2 }}>Skip reasons</Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" gap={1}>
            {Object.entries(stats.skip_reason_breakdown).map(([reason, count]) => (
              <Chip key={reason}
                label={`${failureLabel(reason)}: ${count}`}
                sx={{ height: 26, fontSize: 12, bgcolor: "#F1F5F9", color: B13.ink }} />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
};

const Metric: React.FC<{ label: string; value: number | string; tone?: string; tooltip?: string }> = ({ label, value, tone, tooltip }) => {
  const inner = (
    <Box sx={{ ...surfaceSx, p: 2, borderRadius: 3, cursor: tooltip ? "help" : "default" }}>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: B13Ink.ink3, textTransform: "uppercase", letterSpacing: 0.6 }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: 24, fontWeight: 800, color: tone ?? B13.ink, mt: 0.5, letterSpacing: "-0.02em" }}>
        {value}
      </Typography>
    </Box>
  );
  return tooltip ? <Tooltip title={tooltip} arrow>{inner}</Tooltip> : inner;
};

export default CampaignDetailPage;
